import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ScrollView,
  Alert,
  Platform,
} from 'react-native';
import * as ExpoClipboard from 'expo-clipboard'; // BUG-005: replaced deprecated Clipboard from react-native
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import logger from '../utils/logger';
import QRCode from 'react-native-qrcode-svg';

interface MQTTCredentialsModalProps {
  visible: boolean;
  device: any;
  onClose: () => void;
}

export default function MQTTCredentialsModal({
  visible,
  device,
  onClose,
}: MQTTCredentialsModalProps) {
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const [showQR, setShowQR] = useState(false);

  const isArabic = i18n.language === 'ar';

  if (!device) return null;

  const credentials = {
    'Client ID': device.mqttClientId,
    Username: device.mqttUsername,
    Password: device.mqttPassword,
    'Device ID': device.id,
    Topic: device.mqttTopic || `iot-edge/${device.mqttClientId}/telemetry`,
  };

  const credentialsAr = {
    'معرف العميل': device.mqttClientId,
    'اسم المستخدم': device.mqttUsername,
    'كلمة المرور': device.mqttPassword,
    'معرف الجهاز': device.id,
    الموضوع: device.mqttTopic || `iot-edge/${device.mqttClientId}/telemetry`,
  };

  const displayCredentials = isArabic ? credentialsAr : credentials;

  const copyToClipboard = async (text: string, label: string) => {
    try {
      if (Platform.OS === 'web') {
        await navigator.clipboard.writeText(text);
      } else {
        await ExpoClipboard.setStringAsync(text);
      }
      Alert.alert(
        isArabic ? '✓ تم النسخ' : '✓ Copied',
        `${label} ${isArabic ? 'تم نسخه' : 'copied'}`,
      );
    } catch (error) {
      logger.error('Failed to copy:', error);
      Alert.alert(isArabic ? 'خطأ' : 'Error', isArabic ? 'فشل النسخ' : 'Failed to copy');
    }
  };

  const copyAllCredentials = async () => {
    const allText = Object.entries(credentials)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');

    try {
      if (Platform.OS === 'web') {
        await navigator.clipboard.writeText(allText);
      } else {
        await ExpoClipboard.setStringAsync(allText);
      }
      Alert.alert(
        isArabic ? '✓ تم النسخ' : '✓ Copied',
        isArabic ? 'تم نسخ جميع البيانات' : 'All credentials copied',
      );
    } catch (error) {
      logger.error('Failed to copy all:', error);
    }
  };

  const qrData = JSON.stringify({
    clientId: device.mqttClientId,
    username: device.mqttUsername,
    password: device.mqttPassword,
    deviceId: device.id,
    topic: device.mqttTopic || `iot-edge/${device.mqttClientId}/telemetry`,
  });

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.title, { color: theme.colors.text }]}>
                {isArabic ? '🎉 تم إنشاء الجهاز بنجاح!' : '🎉 Device Created Successfully!'}
              </Text>
              <Text style={[styles.deviceName, { color: theme.colors.textSecondary }]}>
                {device.name}
              </Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close-circle" size={32} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Warning */}
          <View
            style={[
              styles.warningBox,
              { backgroundColor: theme.colors.warning + '20', borderColor: theme.colors.warning },
            ]}
          >
            <Ionicons name="warning" size={24} color={theme.colors.warning} />
            <Text style={[styles.warningText, { color: theme.colors.text }]}>
              {isArabic
                ? '⚠️ احفظ هذه البيانات! لن تظهر مرة أخرى بعد إغلاق هذه النافذة.'
                : "⚠️ Save these credentials! They won't be shown again after closing this window."}
            </Text>
          </View>

          {/* Tabs */}
          <View style={styles.tabs}>
            <TouchableOpacity
              style={[
                styles.tab,
                !showQR && { borderBottomColor: theme.colors.primary, borderBottomWidth: 2 },
              ]}
              onPress={() => setShowQR(false)}
            >
              <Ionicons
                name="list"
                size={20}
                color={!showQR ? theme.colors.primary : theme.colors.textSecondary}
              />
              <Text
                style={[
                  styles.tabText,
                  { color: !showQR ? theme.colors.primary : theme.colors.textSecondary },
                ]}
              >
                {isArabic ? 'النص' : 'Text'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.tab,
                showQR && { borderBottomColor: theme.colors.primary, borderBottomWidth: 2 },
              ]}
              onPress={() => setShowQR(true)}
            >
              <Ionicons
                name="qr-code"
                size={20}
                color={showQR ? theme.colors.primary : theme.colors.textSecondary}
              />
              <Text
                style={[
                  styles.tabText,
                  { color: showQR ? theme.colors.primary : theme.colors.textSecondary },
                ]}
              >
                {isArabic ? 'رمز QR' : 'QR Code'}
              </Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            {!showQR ? (
              /* Text View */
              <View>
                {Object.entries(displayCredentials).map(([key, value]) => (
                  <View
                    key={key}
                    style={[
                      styles.credentialItem,
                      {
                        backgroundColor: theme.colors.background,
                        borderColor: theme.colors.border,
                      },
                    ]}
                  >
                    <View style={styles.credentialInfo}>
                      <Text style={[styles.credentialLabel, { color: theme.colors.textSecondary }]}>
                        {key}
                      </Text>
                      <Text
                        style={[styles.credentialValue, { color: theme.colors.text }]}
                        selectable
                      >
                        {value}
                      </Text>
                    </View>
                    <TouchableOpacity
                      style={[styles.copyButton, { backgroundColor: theme.colors.primary + '20' }]}
                      onPress={() => copyToClipboard(value, key)}
                    >
                      <Ionicons name="copy" size={20} color={theme.colors.primary} />
                    </TouchableOpacity>
                  </View>
                ))}

                {/* Copy All Button */}
                <TouchableOpacity
                  style={[styles.copyAllButton, { backgroundColor: theme.colors.primary }]}
                  onPress={copyAllCredentials}
                >
                  <Ionicons name="copy-outline" size={20} color="#fff" />
                  <Text style={styles.copyAllButtonText}>{isArabic ? 'نسخ الكل' : 'Copy All'}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              /* QR Code View */
              <View style={styles.qrContainer}>
                <View style={[styles.qrBox, { backgroundColor: '#fff' }]}>
                  <QRCode value={qrData} size={240} backgroundColor="#fff" color="#000" />
                </View>
                <Text style={[styles.qrHint, { color: theme.colors.textSecondary }]}>
                  {isArabic
                    ? 'امسح هذا الرمز باستخدام جهازك لاستيراد البيانات تلقائياً'
                    : 'Scan this code with your device to import credentials automatically'}
                </Text>
              </View>
            )}

            {/* Instructions */}
            <View
              style={[
                styles.instructions,
                { backgroundColor: theme.colors.info + '15', borderColor: theme.colors.info },
              ]}
            >
              <Ionicons name="information-circle" size={24} color={theme.colors.info} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.instructionsTitle, { color: theme.colors.text }]}>
                  {isArabic ? 'خطوات التفعيل' : 'Activation Steps'}
                </Text>
                <Text style={[styles.instructionsText, { color: theme.colors.textSecondary }]}>
                  {isArabic
                    ? '1. انسخ بيانات الاتصال أعلاه\n2. أدخلها في إعدادات جهازك\n3. الجهاز سيتصل تلقائياً بالنظام'
                    : '1. Copy the credentials above\n2. Enter them in your device settings\n3. Device will connect automatically to the system'}
                </Text>
              </View>
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={[styles.footer, { borderTopColor: theme.colors.border }]}>
            <TouchableOpacity
              style={[styles.doneButton, { backgroundColor: theme.colors.success }]}
              onPress={onClose}
            >
              <Ionicons name="checkmark-circle" size={24} color="#fff" />
              <Text style={styles.doneButtonText}>{isArabic ? 'تم' : 'Done'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    borderRadius: 24,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  deviceName: {
    fontSize: 14,
    marginTop: 4,
  },
  closeButton: {
    marginLeft: 12,
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    margin: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
  },
  warningText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  tabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    gap: 8,
  },
  tabText: {
    fontSize: 16,
    fontWeight: '600',
  },
  content: {
    padding: 20,
  },
  credentialItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
  },
  credentialInfo: {
    flex: 1,
  },
  credentialLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  credentialValue: {
    fontSize: 15,
    fontWeight: '500',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  copyButton: {
    padding: 10,
    borderRadius: 8,
    marginLeft: 12,
  },
  copyAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    marginTop: 8,
    gap: 8,
  },
  copyAllButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  qrContainer: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  qrBox: {
    padding: 20,
    borderRadius: 16,
    marginBottom: 16,
  },
  qrHint: {
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  instructions: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 20,
    gap: 12,
  },
  instructionsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  instructionsText: {
    fontSize: 14,
    lineHeight: 22,
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
  },
  doneButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  doneButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
});
